import axios from 'axios';
import { redirectToLogin } from '../utils/auth';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://custom-analysis.qsearch.cc';

// 快取相關配置
interface CacheItem {
  data: any;
  timestamp: number;
}

interface CacheStore {
  [key: string]: CacheItem;
}

// 初始化快取存儲
const cache: CacheStore = {};

// 分析資料需要保持新鮮；短期快取只用來避免同頁面重複請求。
const CACHE_TTL = 5 * 60 * 1000;

// 檢查快取是否有效
const isCacheValid = (key: string): boolean => {
  if (!cache[key]) return false;
  
  const now = Date.now();
  const cachedTime = cache[key].timestamp;
  return (now - cachedTime) < CACHE_TTL;
};

// 產生快取鍵
const generateCacheKey = (endpoint: string, params?: any): string => {
  if (!params) return endpoint;
  const paramsString = JSON.stringify(params);
  return `${endpoint}_${paramsString}`;
};

// 從快取取得數據或發起API請求
const getDataWithCache = async (endpoint: string, params?: any): Promise<any> => {
  const cacheKey = generateCacheKey(endpoint, params);
  
  // 如果快取有效，直接返回快取數據
  if (isCacheValid(cacheKey)) {
    console.log(`從快取獲取數據: ${cacheKey}`);
    return cache[cacheKey].data;
  }
  
  // 快取無效，發起API請求
  console.log(`API請求: ${endpoint}`);
  try {
    const response = await apiClient.get(endpoint, { params });
    
    // 將新數據存入快取
    cache[cacheKey] = {
      data: response.data,
      timestamp: Date.now()
    };
    
    return response.data;
  } catch (error) {
    console.error(`API請求失敗: ${endpoint}`, error);
    
    // 如果是 401 錯誤，攔截器已經處理了重定向
    // 這裡只需要重新拋出錯誤
    throw error;
  }
};

// 清除所有快取
export const clearCache = (): void => {
  Object.keys(cache).forEach(key => delete cache[key]);
  console.log('所有快取已被清除');
};

// 建立axios實例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 添加響應攔截器處理 401 錯誤
apiClient.interceptors.response.use(
  (response) => {
    // 成功響應直接返回
    return response;
  },
  (error) => {
    // 檢查是否為 401 未授權錯誤
    if (error.response && error.response.status === 401) {
      console.log('收到 401 未授權響應，重定向到登入頁面');
      // 重定向到登入頁面
      redirectToLogin();
      return Promise.reject(error);
    }
    
    // 其他錯誤正常處理
    return Promise.reject(error);
  }
);

// 定義API請求參數的介面
interface TimeRangeParams {
  start_date?: string;
  end_date?: string;
}

// API服務
export const ApiService = {
  // 取得客戶分析相關數據
  getCustomerAnalytics: async () => {
    return getDataWithCache('/customer_analytics');
  },

  // 取得購買力分析
  getPurchasePowerAnalysis: async () => {
    return getDataWithCache('/purchase_power');
  },

  // 取得RFM分析
  getRFMAnalysis: async () => {
    return getDataWithCache('/rfm_analysis');
  },

  // 取得顧客終身價值分析
  getCustomerLTV: async () => {
    return getDataWithCache('/customer_ltv');
  },

  // 取得客戶詳細數據
  getCustomerDetails: async () => {
    return getDataWithCache('/customer_details');
  },

  // 使用情況分析APIs
  
  // 獲取使用天數
  getUsageDays: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/usage/daily', params);
  },

  // 獲取最後使用日期
  getLastUsageDate: async () => {
    return getDataWithCache('/api/usage/last');
  },

  // 獲取平均每日使用情況
  getAvgDailyUsage: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/usage/avg-daily', params);
  },

  // 獲取資源使用情況
  getResourceUsage: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/usage/resource', params);
  },

  // 獲取每日搜尋次數
  getAmountDaily: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/user/amount_daily', params);
  },

  // 關鍵詞分析APIs

  // 獲取每個組織的關鍵詞數量
  getKeywordCount: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/keywords/count/org', params);
  },

  // 獲取每個關鍵詞被多少個組織使用過
  getTermOrgCount: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/keywords/count/distinct', params);
  },

  // 獲取最常用關鍵詞
  getMostFrequentKeyword: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/keywords/most-frequent', params);
  },

  // 獲取重複關鍵詞
  getRepeatKeyword: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/keywords/repeat', params);
  },

  // 參與度分析API

  // 獲取用戶參與度分數
  getEngagementScore: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/user/engagement_score', params);
  },

  // 組織管理API

  // 獲取組織列表
  getOrgList: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/org/list', params);
  },

  // API QSearch 客戶分析
  getApiQsearchOverview: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/api-qsearch/overview', params);
  },

  getApiQsearchEndpoints: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/api-qsearch/endpoints', params);
  },

  getApiQsearchChannels: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/api-qsearch/channels', params);
  },

  getApiQsearchKeywords: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/api-qsearch/keywords', params);
  },

  // Trend 產品行為分析
  getTrendRequestOverview: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/overview', params);
  },

  getTrendRequestFeatures: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/features', params);
  },

  getTrendRequestActivity: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/activity', params);
  },

  getHotpostsSummaryUsage: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/hotposts-summary', params);
  },

  // Alert 功能行為輪廓（HTTP request log 代理指標）
  getTrendAlert: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/alert', params);
  },

  // 廣告監測每日使用（讀 v_ads_api_usage_daily，含應用層指標）
  getAdsUsage: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/ads/usage', params);
  },

  // 廣告監測各端點的回應時間分位數與錯誤率
  getAdsLatency: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/ads/latency', params);
  },

  // 廣告監測實際被搜尋的關鍵字排行
  getAdsKeywords: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/ads/keywords', params);
  },

  // 廣告監測 API（/api/ads/*）：獨立產品，無渠道/關鍵字維度
  getApiQsearchAds: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/api-qsearch/ads', params);
  },

  // API 客戶每日呼叫量（逐客戶）＋ 7/30 日移動平均
  getApiQsearchDaily: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/api-qsearch/daily', params);
  },

  // Alert 真實設定/條件（讀 trend MySQL，小表，秒回）
  getTrendAlertConfig: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/alert-config', params);
  },

  // Alert 每個 alert 的期間通知數（AlertRecord 千萬列，首次較慢、後端有窗快取）
  getTrendAlertNotifications: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/alert-notifications', params);
  },

  // 文章匯出使用量（讀 trend.ExportTask，含每筆匯出明細，單次取回）
  getTrendExportUsage: async (params?: TimeRangeParams) => {
    return getDataWithCache('/api/trend-requests/export-usage', params);
  },
};
